import React, { useCallback, useEffect, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  PageContainer,
  PageHeader,
  Spinner,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { usageApi } from './usageApi';

const money = (cur, n) => `${cur || 'USD'} ${formatAmount(n)}`;
const pct = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);

const period = (a) => {
  const f = (d) => (d ? new Date(d).toLocaleDateString() : null);
  const s = f(a.periodStart);
  const e = f(a.periodEnd);
  if (s && e) return `${s} – ${e}`;
  return s || e || '—';
};

const varianceTone = (n) => {
  if (n == null) return 'neutral';
  if (n > 0.0001) return 'danger';
  if (n < -0.0001) return 'success';
  return 'neutral';
};

export default function UsageVariancePage() {
  const navigate = useNavigate();
  const t = useT();
  const { hasPermission } = useProperty();
  const canManage = hasPermission('usage.manage');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await usageApi.list()) || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: 'number', header: t('Ref'), width: 120, render: (a) => <span style={{ fontFamily: 'var(--font-mono)' }}>{a.number}</span> },
    { key: 'name', header: t('Analysis'), render: (a) => <span style={{ fontWeight: 600 }}>{a.name}</span> },
    { key: 'period', header: t('Period'), width: 190, render: period },
    { key: 'theo', header: t('Theoretical'), width: 140, align: 'right', render: (a) => money(a.currency, a.theoreticalCost) },
    { key: 'actual', header: t('Actual'), width: 140, align: 'right', render: (a) => money(a.currency, a.actualCost) },
    {
      key: 'variance',
      header: t('Variance'),
      width: 160,
      align: 'right',
      render: (a) => (
        <Badge tone={varianceTone(a.varianceCost)}>
          {a.varianceCost > 0 ? '+' : ''}{money(a.currency, a.varianceCost)} ({pct(a.variancePercent)})
        </Badge>
      ),
    },
    { key: 'flagged', header: t('Flagged'), width: 90, align: 'right', render: (a) => (a.flaggedCount > 0 ? <Badge tone="warning">{a.flaggedCount}</Badge> : '—') },
  ];

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Usage & variance')}
        description={t('Compare theoretical ingredient usage (from sales × recipes) against actual usage (from the inventory ledger) to surface over-portioning, waste and shrink.')}
        icon={<Icon.ArrowLeftRight size={22} />}
        actions={
          canManage && (
            <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => navigate('/usage-variance/new')}>
              {t('New analysis')}
            </Button>
          )
        }
      />

      {loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          rowKey={(a) => a.id}
          density="comfortable"
          onRowClick={(a) => navigate(`/usage-variance/${a.id}`)}
          emptyState={
            <EmptyState
              icon={<Icon.ArrowLeftRight size={28} />}
              title={t('No usage analyses yet')}
              description={t("Pick a period, enter the menu items sold, and we'll compare expected vs actual ingredient usage to find where food cost is leaking.")}
              action={canManage && <Button variant="primary" onClick={() => navigate('/usage-variance/new')}>{t('New analysis')}</Button>}
            />
          }
        />
      )}
    </PageContainer>
  );
}
